import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import { RootState } from "./redux/store";
import { lazyWithReload } from "./utils/lazy-loader/lazy-loader";
import * as Sentry from "@sentry/react";
import { setSentryUser } from "./observability/sentry/sentry.identity";
import {
  ClientType,
  login,
  setOutlookIntegration,
} from "./redux/slices/authSlice";
import {
  useGetUserDetailsMutation,
  useClientConfigMutation,
  useGetUserClientsMutation,
  useGetIntegrationStatusMutation,
} from "./redux/services/authApi";
import { SocketProvider } from "./providers/SocketProvider";
import { getClientFeatureConfig } from "./utils/functions/get-client-feature-config";
import { getAccessibleRoutes } from "./utils/functions/get-accessible-routes";
import {
  useHasVerbaiqChatAccess,
  useHasVerbaiqAnalyticsAccess,
} from "./hooks/useVerbaiqAccess";
import {
  getClientDomain,
  redirectToNewClient,
} from "./utils/functions/client-code";
import {
  initModaTracking,
  setUserDetailsOnLogin,
} from "./utils/amplitude/amplitude";
import { capitalizeFirstLetter } from "./utils/functions/formatters";
import AnnouncementBanner from "./components/AnnouncementBanner/AnnouncementBanner";
import {
  ANNOUNCEMENT_BANNER_DISMISSED_KEY,
  getAnnouncementSlides,
} from "./utils/constants/announcementBannerInfo";

const NotificationDrawer = lazyWithReload(
  () => import("./components/NotificationDrawer/NotificationDrawer")
);

const CallStateManager = lazyWithReload(
  () => import("./components/TwilioCalling/CallStateManager")
);

const CustomLoader = lazyWithReload(
  () => import("./components/Common/CustomLoader")
);
const SidebarLayout = lazy(
  () => import("./components/Sidebar/components/SidebarLayout")
);
const ComposeEmailModal = lazyWithReload(
  () => import("./components/ComposeEmailModal/ComposeEmailModal")
);
const MiniComposeEmailModal = lazyWithReload(
  () => import("./components/ComposeEmailModal/MiniComposeEmailModal")
);

const NavigationBlocker = lazyWithReload(
  () => import("./components/NavigationBlocker/NavigationBlocker")
);
const SentryRoutes = Sentry.wrapReactRouterRouting(Routes);

function App() {
  const dispatch = useDispatch();
  const auth = useSelector((store: RootState) => store.auth);
  const hasVerbaiqChatAccess = useHasVerbaiqChatAccess();
  const hasVerbaiqAnalyticsAccess = useHasVerbaiqAnalyticsAccess();
  const [getUserDetails] = useGetUserDetailsMutation();
  const [clientConfig] = useClientConfigMutation();
  const [getUserClients] = useGetUserClientsMutation();
  const [getIntegrationStatus] = useGetIntegrationStatusMutation();

  const [isLoading, setIsLoading] = useState(true);
  const [showAnnouncementBanner, setShowAnnouncementBanner] = useState(() => {
    try {
      return (
        window.sessionStorage.getItem(ANNOUNCEMENT_BANNER_DISMISSED_KEY) !==
        "true"
      );
    } catch {
      return true;
    }
  });

  const fetchUserDetails = useCallback(async () => {
    if (auth.isLoggedIn) {
      return;
    }
    const userId = Cookies.get("userId") ? Cookies.get("userId") : null;

    if (!userId) {
      setIsLoading(false);
      return;
    }

    try {
      const [userRes, clientsRes, configRes] = await Promise.allSettled([
        getUserDetails(userId).unwrap(),
        getUserClients({}).unwrap(),
        clientConfig({}).unwrap(),
      ]);

      const allFulfilled =
        userRes.status === "fulfilled" &&
        clientsRes.status === "fulfilled" &&
        configRes.status === "fulfilled";

      if (!allFulfilled) {
        setIsLoading(false);
        return;
      }

      const usersData = userRes.value;
      const clientList = clientsRes.value;
      const clientsData = configRes.value;
      const clientDomain = getClientDomain();

      const matchingClient = (clientList as ClientType[]).find(
        (client) => client.client_code === clientDomain
      );
      if (!matchingClient) {
        redirectToNewClient(usersData?.client_code);
        return;
      }

      dispatch(
        login({
          user: {
            _id: usersData?._id,
            name: usersData?.name ?? null,
            email: usersData?.email,
            phone_number: usersData?.phone_number ?? null,
            profile_picture: usersData?.profile_picture ?? null,
            country_code: usersData?.country_code ?? null,
            client_code: usersData?.client_code ?? null,
            current_client_code: clientDomain ?? null,
            client_list: clientList ?? null,
            role: usersData?.role,
            org_code: usersData?.org_code ?? null,
            is_agent: usersData?.is_agent ?? false,
            helpdesk_category_access:
              usersData?.helpdesk_category_access ?? null,
            helpdesk_category_update_access:
              usersData?.helpdesk_category_update_access ?? null,
            properyIds: usersData?.property_ids ?? null,
            // These two were previously missing here entirely — a page refresh silently wiped
            // VerbaIQ access to undefined for the whole session until the next full sign-in.
            verbaiq_chat_access_client_codes:
              usersData?.verbaiq_chat_access_client_codes ?? null,
            verbaiq_analytics_access_client_codes:
              usersData?.verbaiq_analytics_access_client_codes ?? null,
          },
          client: {
            ...clientsData,
            email_signature: undefined,
            client_logo: clientsData?.widget_config?.client_logo_url ?? null,
          },
          isVerified: usersData?.isVerified ?? true,
          organization: usersData?.org_code ?? null,
        })
      );
      const userData = {
        // `/users/get-user-details` returns `_id` (the login endpoint returns the same
        // identifier as `user_id`). Reading `user_id` here yielded undefined on every
        // refresh, and amplitude.setUserId(undefined) force-clears the identity.
        user_id: usersData?._id,
        name: usersData?.name || "unknown",
        email: usersData?.email,
        role: usersData?.role,
        org_id: clientsData?.client_code || "unknown",
        org_name:
          capitalizeFirstLetter(clientsData?.client_name) ||
          "Unknown Organization",
        team_id: usersData?.team_id || "unknown",
      };
      initModaTracking(clientsData?.client_code);
      setUserDetailsOnLogin(userData, "session_started");
      setSentryUser(userData);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }, [auth.isLoggedIn, getUserDetails, getUserClients, clientConfig, dispatch]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  useEffect(() => {
    if (auth.isLoggedIn && auth.user?.email) {
      getIntegrationStatus({ user_email: auth.user.email })
        .unwrap()
        .then((integrationData) => {
          dispatch(setOutlookIntegration(integrationData));
        })
        .catch(() => {
          dispatch(setOutlookIntegration(null));
        });
    }
  }, [auth.isLoggedIn, auth.user?.email]);

  const handleDismissAnnouncementBanner = () => {
    setShowAnnouncementBanner(false);
    try {
      window.sessionStorage.setItem(ANNOUNCEMENT_BANNER_DISMISSED_KEY, "true");
    } catch {
      return;
    }
  };

  // VerbaIQ access is only meaningful post-login, so the VerbaIQ slide (and the 2-slide carousel)
  // only appears once the user is logged in with chat access; otherwise the ticker is the single
  // EU AI Act announcement (including on the login page).
  const announcementSlides = useMemo(
    () => getAnnouncementSlides(auth.isLoggedIn ?? false, hasVerbaiqChatAccess),
    [auth.isLoggedIn, hasVerbaiqChatAccess]
  );

  const accessibleRoutes = useMemo(() => {
    const clientFeatureConfig = auth?.isLoggedIn
      ? getClientFeatureConfig(auth)
      : null;

    return getAccessibleRoutes(
      clientFeatureConfig,
      auth?.user?.role,
      auth?.isLoggedIn ?? false,
      hasVerbaiqChatAccess,
      hasVerbaiqAnalyticsAccess
    );
  }, [auth, hasVerbaiqChatAccess, hasVerbaiqAnalyticsAccess]);

  if (isLoading) {
    return;
  }

  return (
    <BrowserRouter>
      <SocketProvider>
        <div className="max-w-screen h-screen flex flex-col overflow-hidden">
          {showAnnouncementBanner && announcementSlides.length > 0 && (
            <AnnouncementBanner
              slides={announcementSlides}
              onDismiss={handleDismissAnnouncementBanner}
            />
          )}
          <Suspense
            fallback={
              <div className="flex-1 w-full flex items-center justify-center">
                <CustomLoader />
              </div>
            }
          >
            <SentryRoutes>
              {accessibleRoutes.map((route, index: number) => {
                const Component = route.component;
                if ("redirectTo" in route && route?.redirectTo) {
                  return (
                    <Route
                      key={index}
                      path={route?.path}
                      element={<Navigate to={route?.redirectTo} />}
                    />
                  );
                }
                if (!auth.isLoggedIn) {
                  return (
                    <Route
                      key={index}
                      path={route.path}
                      element={
                        <div className="flex-1 min-h-0 flex flex-col justify-between">
                          {Component && <Component />}
                        </div>
                      }
                    />
                  );
                }
                return (
                  <Route
                    key={index}
                    path={route.path}
                    element={
                      <div className="flex-1 min-h-0">
                        {route.layout === "standalone" ? (
                          Component && <Component />
                        ) : (
                          <SidebarLayout>
                            {Component && <Component />}
                          </SidebarLayout>
                        )}
                      </div>
                    }
                  />
                );
              })}
            </SentryRoutes>
          </Suspense>
        </div>

        <Suspense
          fallback={
            <div className="h-screen w-full flex items-center justify-center">
              <CustomLoader />
            </div>
          }
        >
          <NotificationDrawer />
          <ComposeEmailModal />
          <MiniComposeEmailModal />
          <NavigationBlocker />
          <CallStateManager />
        </Suspense>
        <Toaster position="bottom-right" reverseOrder={false} />
      </SocketProvider>
    </BrowserRouter>
  );
}

export default App;
